import {
  LinkOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Col, Input, Row, Space, Splitter } from 'antd';
import { React, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useGetProducts } from '../../../../utils/api/products/useGetProducts';
import { useProductsSearch } from '../../../../utils/zustand';
import ProductList from './ProductTable';
import ProductFilters from './filters';

const DEFAULT_SIDEBAR_WIDTH = 280;

const Products = () => {
  const { t } = useTranslation('products');
  const { marketplace } = useParams();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const { searchInput, setSearchInput } = useProductsSearch();
  const [search, setSearch] = useState(searchInput);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sizes, setSizes] = useState([DEFAULT_SIDEBAR_WIDTH, 'auto']);
  const navigate = useNavigate();

  // Calculer l'offset basé sur la page courante
  const offset = (currentPage - 1) * pageSize;

  // Formater les filtres pour l'API (tableaux -> chaîne séparée par virgules)
  const formattedFilters = {
    categories: filters.category?.length
      ? filters.category.join(',')
      : undefined,
    brands: filters.brand?.length ? filters.brand.join(',') : undefined,
    range: filters.range,
    composition: filters.composition,
    types: filters.type?.length ? filters.type.join(',') : undefined,
    categories_mp: filters.mp_category?.length
      ? filters.mp_category.join(',')
      : undefined,
    categories_mp_to_exclude: filters.not_in_mp_category?.length
      ? filters.not_in_mp_category.join(',')
      : undefined,
  };

  const { products, count, isLoading } = useGetProducts({
    search,
    ...formattedFilters,
    ...(marketplace && { marketplace__name: marketplace }), // Filtrer par marketplace si présent
    offset,
    limit: pageSize,
  });

  // Debounce : search with React Query
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset to first page when search, filters, or marketplace change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, filters, marketplace]);

  const toggleSidebar = () => {
    if (sidebarOpen) {
      setSizes([0, 'auto']);
    } else {
      setSizes([DEFAULT_SIDEBAR_WIDTH, 'auto']);
    }
    setSidebarOpen(!sidebarOpen);
  };

  const handleResize = (newSizes) => {
    setSizes(newSizes);
    // Si la taille du panneau gauche est très petite, on considère qu'il est fermé
    if (newSizes[0] < 50) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  };

  // TODO: handle margin / padding to gain some space
  return (
    <Splitter onResize={handleResize} style={{ height: 'calc(100vh - 64px)' }}>
      <Splitter.Panel
        size={sizes[0]}
        min={0}
        max={400}
        collapsible
        style={{
          overflow: 'auto',
          transition: 'width 0.2s ease',
        }}
      >
        <div
          style={{
            padding: sidebarOpen ? '16px' : 0,
            height: '100%',
            backgroundColor: '#fafafa',
            borderRight: '1px solid #f0f0f0',
            display: sidebarOpen ? 'block' : 'none',
          }}
        >
          <ProductFilters t={t} setFilters={setFilters} filters={filters} />
        </div>
      </Splitter.Panel>
      <Splitter.Panel size={sizes[1]} style={{ overflow: 'auto' }}>
        <div style={{ padding: '0 16px' }}>
          <Row gutter={[16, 16]}>
            <Col span={12} justify="start" style={{ paddingLeft: 0 }}>
              <Space align="center">
                <Button
                  type="text"
                  icon={
                    sidebarOpen ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />
                  }
                  onClick={toggleSidebar}
                  title={
                    sidebarOpen ? 'Fermer le panneau' : 'Ouvrir le panneau'
                  }
                />
                <h1 style={{ margin: 0 }}>
                  {marketplace
                    ? `${t('list.title')} - ${marketplace}`
                    : t('list.title')}
                </h1>
              </Space>
            </Col>
          </Row>
          <div style={{ height: 16 }} />
          <Row gutter={[16, 16]} align="middle">
            <Col
              span={12}
              style={{
                display: 'flex',
                justifyContent: 'start',
                margin: '8px 0',
              }}
            >
              <Input
                size="small"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder={t('filters.search_placeholder')}
                style={{ width: 400 }}
                allowClear
              />
            </Col>
            <Col span={24} style={{ margin: 0 }}>
              <div
                style={{
                  minHeight: '42px',
                  padding: '8px 8px',
                  marginBottom: '8px',
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor:
                    selectedProducts.length > 0 ? '#91caff4f' : 'transparent',
                }}
              >
                <Space>
                  <span>Produits sélectionnés ({selectedProducts.length})</span>
                  {selectedProducts.length > 0 && (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => setSelectedProducts([])}
                    >
                      Désélectionner tout
                    </Button>
                  )}
                </Space>
                {selectedProducts.length > 0 && (
                  <Space>
                    <Button type="default" size="small">
                      Exporter
                    </Button>
                    <Button type="default" size="small">
                      Mettre à jour
                    </Button>
                    <Button
                      type="default"
                      size="small"
                      onClick={() => {
                        navigate('/dashboard/products/mp-association');
                      }}
                      icon={<LinkOutlined />}
                    >
                      Associer à une catégorie MP
                    </Button>
                  </Space>
                )}
              </div>
            </Col>
          </Row>
          <ProductList
            selectedProducts={selectedProducts}
            setSelectedProducts={setSelectedProducts}
            products={products}
            count={count}
            isLoading={isLoading}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      </Splitter.Panel>
    </Splitter>
  );
};

export default Products;
